// @file Vuex Store module for Settings panels and subpanels
import {
  checkWallAvailability,
  saveWall,
  validateWall,
  fetchThemes,
  fetchMapThemes as arvoFetchMapThemes,
  Wall,
  WallBackgroundCollection,
  WallTheme,
  JsonAPIResponse,
  JsonAPIResource,
} from '@padlet/arvo'
import { captureMessage } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import { getCompatibleThemeId } from 'bits/map_theme'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import merge from 'lodash.merge'
import Vue from 'vue'
import { ActionContext, Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

enum SettingsSubpanel {
  Main = 'main',
  MapStyle = 'mapstyle',
  Wallpaper = 'wallpaper',
  Icon = 'icon',
  Address = 'address',
  Reactions = 'reactions',
}

interface WallpaperPicker {
  title: string
  backgrounds: WallBackgroundCollection
  xWallpaperNames: boolean
}

interface SettingsPanelState {
  activePanel: SettingsSubpanel | null
  isSaving: boolean
  previewAttributes: Wall | {}
  saveErrorMessage: string | null
  wallpaperPicker: WallpaperPicker | null
  xCustomWallpaperPicker: boolean
  xCustomIconPicker: boolean
  isWallNameAvailable: boolean
  isWallNameValid: boolean
  isReactionDataValid: boolean
  arePreviewAttributesValid: boolean
  lastAvailableWallName: string | null
  wallThemes: WallTheme[]
  isFetchingWallThemes: boolean
  allMapThemes: WallTheme[]
  isFetchingMapThemes: boolean
}

type SettingsActionContext = ActionContext<SettingsPanelState, RootState>

const defaultState = (): SettingsPanelState => ({
  activePanel: null,
  isSaving: false,
  previewAttributes: {},
  saveErrorMessage: null,
  wallpaperPicker: null,
  xCustomWallpaperPicker: false,
  xCustomIconPicker: false,
  isWallNameAvailable: true,
  isWallNameValid: true,
  isReactionDataValid: true,
  arePreviewAttributesValid: true,
  lastAvailableWallName: null,
  wallThemes: [],
  isFetchingWallThemes: false,
  allMapThemes: [],
  isFetchingMapThemes: false,
})

const SettingsModule: Module<SettingsPanelState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    arePreviewAttributesDirty(state, _, rootState): boolean {
      return !isEqual(state.previewAttributes, rootState.wall)
    },
    isOpen(state): boolean {
      return state.activePanel !== null
    },
    activePanel(state): SettingsSubpanel | null {
      return state.activePanel
    },
    previewAttributes(state): { [key: string]: any } {
      return state.previewAttributes
    },
    isSaving(state): boolean {
      return state.isSaving
    },
    isPreviewing(state): boolean {
      return Object.keys(state.previewAttributes).length > 0
    },
    saveErrorMessage(state): string | null {
      return state.saveErrorMessage
    },
    wallpaperPicker(state): WallpaperPicker | null {
      return state.wallpaperPicker
    },
    xWallpaperPicker(state): boolean {
      return state.wallpaperPicker !== null
    },
    xCustomWallpaperPicker(state): boolean {
      return state.xCustomWallpaperPicker
    },
    xCustomIconPicker(state): boolean {
      return state.xCustomIconPicker
    },
    wallThemes(state): WallTheme[] {
      return state.wallThemes
    },
    mapThemesDictionary(state): Record<number, WallTheme> {
      return state.allMapThemes.reduce((obj, item) => {
        obj[item.id] = item
        return obj
      }, {})
    },
    mapTheme(state, getters, rootState, rootGetters): WallTheme {
      return getters.mapThemesDictionary[getCompatibleThemeId(rootGetters.themeId)]
    },
  },
  mutations: {
    RESET_SETTINGS_STATE(state): void {
      // keep the fetched themes
      Object.assign(state, { ...defaultState(), wallThemes: state.wallThemes, allMapThemes: state.allMapThemes })
    },
    START_PREVIEWING_ATTRIBUTES(state, previewAttributes: Wall): void {
      state.previewAttributes = previewAttributes
    },
    SET_ACTIVE_PANEL(state, panel: SettingsSubpanel): void {
      state.activePanel = panel
    },
    SHOW_WALLPAPER_PICKER(state, options: WallpaperPicker): void {
      state.wallpaperPicker = options
    },
    HIDE_WALLPAPER_PICKER(state): void {
      state.wallpaperPicker = null
    },
    SHOW_CUSTOM_WALLPAPER_PICKER(state): void {
      state.xCustomWallpaperPicker = true
    },
    HIDE_CUSTOM_WALLPAPER_PICKER(state): void {
      state.xCustomWallpaperPicker = false
    },
    SHOW_CUSTOM_ICON_PICKER(state): void {
      state.xCustomIconPicker = true
    },
    HIDE_CUSTOM_ICON_PICKER(state): void {
      state.xCustomIconPicker = false
    },
    SETTINGS_SAVE(state): void {
      state.isSaving = true
      state.saveErrorMessage = null
    },
    SETTINGS_SAVE_SUCCESS(state): void {
      state.isSaving = false
    },
    SETTINGS_SAVE_ERROR(state, message): void {
      state.isSaving = false
      state.saveErrorMessage = message
    },
    UPDATE_PREVIEW_ATTRIBUTES(state, attributes): void {
      state.previewAttributes = merge(cloneDeep(state.previewAttributes), attributes)
    },
    UPDATE_WALL_AVAILABILITY(state, { isAvailable }: { isAvailable: boolean }): void {
      state.isWallNameAvailable = isAvailable
    },
    UPDATE_WALL_NAME_VALIDITY(state, { isValid }: { isValid: boolean }): void {
      state.isWallNameValid = isValid
    },
    UPDATE_WALL_REACTION_DATA_VALIDITY(state, { isValid }): void {
      state.isReactionDataValid = isValid
    },
    UPDATE_PREVIEW_ATTRIBUTES_VALIDITY(state, { arePreviewAttributesValid }): void {
      state.arePreviewAttributesValid = arePreviewAttributesValid
    },
    UPDATE_LAST_CHECKED_NAME(state: SettingsPanelState, name: string): void {
      state.lastAvailableWallName = name
    },
    START_FETCH_WALL_THEMES(state): void {
      state.isFetchingWallThemes = true
    },
    FETCHED_WALL_THEMES(state, wallThemes): void {
      Vue.set(state, 'wallThemes', [...wallThemes])
      state.isFetchingWallThemes = false
    },
    START_FETCH_MAP_THEMES(state): void {
      state.isFetchingMapThemes = true
    },
    FETCHED_MAP_THEMES(state, mapThemes): void {
      Vue.set(state, 'allMapThemes', [...mapThemes])
      state.isFetchingMapThemes = false
    },
  },
  actions: {
    startWallAttributesPreview({ commit, rootState }): void {
      commit('START_PREVIEWING_ATTRIBUTES', { ...rootState.wall })
    },
    stopWallAttributesPreview({ commit }): void {
      commit('RESET_SETTINGS_STATE')
    },
    hideSettingsPanel({ commit }): void {
      commit('RESET_SETTINGS_STATE')
    },
    showSettingsPanel({ dispatch }, activePanel: SettingsSubpanel = SettingsSubpanel.Main): void {
      dispatch('startWallAttributesPreview')
      dispatch('changeActivePanel', activePanel)
    },
    changeActivePanel({ commit }, panel: SettingsSubpanel): void {
      commit('SET_ACTIVE_PANEL', panel)
    },
    async saveSettings({ commit, dispatch, state, getters, rootGetters }): Promise<any> {
      try {
        if (!getters.isPreviewing) captureMessage(`saveSettings is dispatched when not previewing`)

        commit('SETTINGS_SAVE')
        const updatedWall = await saveWall(state.previewAttributes as Wall)
        dispatch('updateWall', updatedWall, { root: true })
        dispatch('updateAndValidatePreviewAttributes', updatedWall)
        commit('SETTINGS_SAVE_SUCCESS')

        if (rootGetters['onboarding/isWallOnboardingFlow']) {
          dispatch('onboarding/goOnboardingNextStage', null, { root: true })
        }
      } catch {
        dispatch('setSaveSettingError', { message: __('Sorry! Something went wrong. Please try saving again.') })
      }
    },
    setSaveSettingError({ commit }, { message }): void {
      commit('SETTINGS_SAVE_ERROR', message)
    },
    toggleSettingsPanel({ dispatch, getters }, panel: SettingsSubpanel): void {
      if (getters.isOpen) {
        dispatch('hideSettingsPanel')
      } else {
        dispatch('showSettingsPanel', panel)
      }
    },
    updatePreviewAttributes({ commit, getters }: SettingsActionContext, attributes): void {
      if (!getters.isPreviewing) {
        const context = { attributes }
        captureMessage(`updatePreviewAttributes is dispatched when not previewing`, { context })
      }

      commit('UPDATE_PREVIEW_ATTRIBUTES', attributes)
    },
    updateAndValidatePreviewAttributes({ getters, dispatch }: SettingsActionContext, attributes): void {
      dispatch('updatePreviewAttributes', attributes)
      if (getters.arePreviewAttributesDirty) {
        // next tick
        Vue.nextTick((): Promise<void> => dispatch('updateWallValidity'))
      }
    },
    showWallpaperPicker({ commit }, options: WallpaperPicker): void {
      commit('SHOW_WALLPAPER_PICKER', options)
    },
    hideWallpaperPicker({ commit }): void {
      commit('HIDE_WALLPAPER_PICKER')
    },
    updateBackground({ dispatch, getters, rootState }, { url, fill, effect }): void {
      const reuseOriginalMetadata = url === rootState.wall.background.url
      const background = reuseOriginalMetadata
        ? { ...rootState.wall.background, fill, effect }
        : { ...getters.previewAttributes.background, url, fill, effect }
      dispatch('updatePreviewAttributes', { background })
    },
    updateBackgroundMetadata({ dispatch }, { dominantColourAsRgb, imageLuminance }): void {
      const attributes = {
        background: {
          dominant_color_as_rgb: dominantColourAsRgb,
          luminance_name: imageLuminance,
        },
      }
      dispatch('updatePreviewAttributes', attributes)
    },
    pickCustomBackground({ dispatch }, { url }): void {
      dispatch('updateBackground', { url, fill: 'cover' })
      dispatch('imagePicker/clearActiveSource', null, { root: true })
    },
    showCustomBackgroundPicker({ dispatch, commit, rootGetters }): void {
      commit('SHOW_CUSTOM_WALLPAPER_PICKER')
      const payload = {
        onPickAction: 'settings/pickCustomBackground',
        initialSearchQuery: rootGetters.title,
        disallowGifSearch: true,
      }
      dispatch('imagePicker/showImagePickerSources', payload, { root: true })
    },
    hideCustomBackgroundPicker({ dispatch, commit }): void {
      commit('HIDE_CUSTOM_WALLPAPER_PICKER')
      dispatch('imagePicker/hideImagePickerSources', null, { root: true })
    },
    pickCustomIconAndGoBack({ dispatch }, { url }): void {
      dispatch('updateAndValidatePreviewAttributes', { portrait: url || '' })
      dispatch('changeActivePanel', SettingsSubpanel.Main)
      dispatch('hideCustomIconPicker')
    },
    showCustomIconPicker({ dispatch, commit, rootGetters }): void {
      commit('SHOW_CUSTOM_ICON_PICKER')
      const payload = {
        onPickAction: 'settings/pickCustomIconAndGoBack',
        initialSearchQuery: rootGetters.title,
      }
      dispatch('imagePicker/showImagePickerSources', payload, { root: true })
    },
    hideCustomIconPicker({ dispatch, commit }): void {
      commit('HIDE_CUSTOM_ICON_PICKER')
      dispatch('imagePicker/hideImagePickerSources', null, { root: true })
    },
    async resetWallNameToKey({ dispatch, rootGetters }): Promise<void> {
      dispatch('updatePreviewAttributes', { name: rootGetters.publicKey })
      await dispatch('updateWallValidity')
    },
    async updateWallValidity({ commit, state, rootState, rootGetters }: SettingsActionContext): Promise<void> {
      try {
        // reset validity
        commit('UPDATE_WALL_NAME_VALIDITY', { isValid: true })
        commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: true })
        let arePreviewAttributesValid = true
        const id = Number(rootGetters.wallId)
        const previewAttributes = state.previewAttributes as Wall
        await validateWall({ id, ...previewAttributes })

        // check if name is different and update wall name availability
        const { name } = state.previewAttributes as Wall
        if (name && name !== rootState.wall.name && name !== state.lastAvailableWallName) {
          // Check against last queried name so we don't make wasted queries against
          // a name that has already been validated in this round of settings updates.
          const isAvailable = await checkWallAvailability(name, rootGetters.wallId)
          commit('UPDATE_WALL_AVAILABILITY', { isAvailable })
          commit('UPDATE_LAST_CHECKED_NAME', name)
          arePreviewAttributesValid = arePreviewAttributesValid && isAvailable
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid })
      } catch (e) {
        const reasons = e.reasons || []
        const isWallNameInvalid = reasons.find((reason: { field: string }): boolean => reason.field === 'name')
        if (isWallNameInvalid) {
          commit('UPDATE_WALL_NAME_VALIDITY', { isValid: false })
        }
        const isReactionDataInvalid = reasons.find(
          (reason: { field: string }): boolean => reason.field === 'reaction_data',
        )
        if (isReactionDataInvalid) {
          commit('UPDATE_WALL_REACTION_DATA_VALIDITY', { isValid: false })
        }
        commit('UPDATE_PREVIEW_ATTRIBUTES_VALIDITY', { arePreviewAttributesValid: false })
      }
    },
    async fetchWallThemes({ commit, state }): Promise<void> {
      if (state.wallThemes.length > 0 || state.isFetchingWallThemes) {
        return
      }

      commit('START_FETCH_WALL_THEMES')
      const themesResponse: JsonAPIResponse<WallTheme> = await fetchThemes()
      const allThemes = (themesResponse.data as JsonAPIResource<WallTheme>[]).map(
        (wallTheme: JsonAPIResource<WallTheme>) => wallTheme.attributes,
      )
      commit('FETCHED_WALL_THEMES', allThemes)
    },
    async fetchMapThemes({ commit, state, rootGetters }): Promise<void> {
      if (!rootGetters['isMap'] || state.allMapThemes.length > 0 || state.isFetchingMapThemes) {
        return
      }

      commit('START_FETCH_MAP_THEMES')
      const themesResponse: JsonAPIResponse<WallTheme> = await arvoFetchMapThemes()
      const allThemes = (themesResponse.data as JsonAPIResource<WallTheme>[]).map(
        (mapTheme: JsonAPIResource<WallTheme>) => mapTheme.attributes,
      )
      commit('FETCHED_MAP_THEMES', allThemes)
    },
  },
}
export default SettingsModule
export { SettingsSubpanel, SettingsPanelState }
